import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { EnumDropdownItemVariant, Icon, type IDropdownItem, Typography } from 'shared/ui'
import {
  LINK_COMPLIANCE_LEARN_MORE_LINK,
  LINK_COMPLIANCE_TOLL_FREE_LEARN_MORE_LINK,
  LINK_INTERNATIONAL_COMPLIANCE_GUIDE,
} from 'shared/constants/links'
import { type IAlertProps } from 'shared/ui/Alert/types'
import { ChooseNumberTabs } from 'entities/Phone/ui/ChooseNumberTabs/ChooseNumberTabs'
import { ITabsEnum } from 'entities/Phone/types'
import { type IResponseTollFree } from 'entities/Compliance'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ChooseNumberTabsStore } from 'entities/Phone/ui/ChooseNumberTabs/store/ChooseNumberTabsStore'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import styles from './styles.module.scss'

const assignLaterId = -1

type IChooseNumberCommonProps = {
  chooseNumberTabsStore: ChooseNumberTabsStore
  useCases: IResponseTollFree[]
  selectedUseCaseId: number | null
  nextDisabled?: boolean
  setUseCase: (useCase: number) => void
  nextClick?: () => void
  isHideNextButton?: boolean
}

export const ChooseNumberCommon = observer(
  ({
    chooseNumberTabsStore,
    useCases,
    selectedUseCaseId,
    nextDisabled,
    setUseCase,
    nextClick,
    isHideNextButton = false,
  }: IChooseNumberCommonProps) => {
    const { complianceStore, featureFlagsStore } = useEntitiesContext()
    const [showAlert, setShowAlert] = useState(true)
    const { activeTab, countryStore } = chooseNumberTabsStore

    const laterItem = {
      id: assignLaterId,
      label: 'Add use case later',
      variant: EnumDropdownItemVariant.Custom,
      className: styles.hiddenOption,
      renderOption: () => (
        <Typography
          tag='div'
          ariaLabel='addUseCaseLater'
          variant='body-md-regular'
          color='var(--content-primary-primary)'
        >
          Add use case later
        </Typography>
      ),
    }

    const completedUseCases = useCases.filter((item) => item.completed)

    const items: IDropdownItem[] = completedUseCases.map((item) => ({
      id: item.id,
      label: item.title || '',
      value: item.title || '',
      data: item,
      variant: EnumDropdownItemVariant.Custom,
      height: '51px',
      className: styles.option,
      renderOption: (option) => {
        const { numbers, title } = option.data as IResponseTollFree
        const titleValue = title || ''

        return (
          <div className={styles.optionWrap}>
            <div className={styles.optionWrap__iconBox}>{titleValue[0].toUpperCase()}</div>

            <div className={styles.optionWrap__info}>
              <Typography
                ariaLabel='name'
                color='var(--content-primary-primary)'
                variant='body-md-regular'
              >
                {title}
              </Typography>
              <Typography
                ariaLabel='phone'
                color='var(--content-primary-tertiary)'
                variant='body-sm-regular'
              >
                {numbers.length} {numbers.length === 1 ? 'number' : 'numbers'}
              </Typography>
            </div>
          </div>
        )
      },
    }))

    const activeLocalTab = chooseNumberTabsStore.activeTab === ITabsEnum.LOCAL

    const alertTitle = activeLocalTab
      ? 'You are required by the carriers to register for A2P 10DLC'
      : 'You are required by the carriers to verify your toll-free numbers'

    const alertDesc = activeLocalTab
      ? 'Register to stay compliant and get higher messaging limits. A2P 10DLC is required if you want to send messages to United States.'
      : 'Verify your toll-free numbers to avoid carrier filtering and ensure better deliverability. Toll-free verification is required if you want to send messages to United States or Canada.'

    const link = activeLocalTab
      ? LINK_COMPLIANCE_LEARN_MORE_LINK
      : LINK_COMPLIANCE_TOLL_FREE_LEARN_MORE_LINK

    const { internationalRegulatoryBundlesStore } = complianceStore
    const isUKComplianceNotCompleted =
      chooseNumberTabsStore.isUKSelected &&
      featureFlagsStore.uk_compliance &&
      !internationalRegulatoryBundlesStore.isUKApproved

    const isUKComplianceCompleted =
      chooseNumberTabsStore.isUKSelected &&
      featureFlagsStore.uk_compliance &&
      internationalRegulatoryBundlesStore.isUKApproved

    const isUKSelected = featureFlagsStore.uk_compliance && chooseNumberTabsStore.isUKSelected

    const alertProps = (): IAlertProps | null => {
      if (isUKSelected) {
        return internationalRegulatoryBundlesStore.isUKApproved
          ? {
              className: styles.alert,
              item: {
                type: 'warning' as const,
                desc: 'You can still send SMS and make calls.',
                title: 'MMS can’t be sent from UK mobile numbers.',
              },
              marginTop: '32px',
            }
          : {
              className: styles.alert,
              item: {
                type: 'infoBlue' as const,
                desc: 'Before you can purchase a UK mobile number, you must complete the UK: Regulatory bundle. Once approved, you’ll be able to buy and use UK numbers for messaging and calls.',
                title: 'Carriers require UK compliance before purchase',
              },
              actions: [
                {
                  type: 'primary',
                  variant: 'navLink',
                  appType: 'react',
                  text: 'Get approved',
                  link: internationalRegulatoryBundlesStore.pathToCurrentStep,
                },
                {
                  type: 'secondary',
                  variant: 'link',
                  text: 'Learn more',
                  link: LINK_INTERNATIONAL_COMPLIANCE_GUIDE,
                },
              ],
              marginTop: '32px',
            }
      }

      if (!complianceStore.is10DLCNotRegistered && activeLocalTab) {
        return null
      }

      return {
        className: styles.alert,
        onClose: () => {
          setShowAlert(false)
        },
        item: {
          type: 'infoBlue' as const,
          desc: alertDesc,
          title: alertTitle,
        },
        actions: [
          {
            type: 'primary',
            variant: 'link',
            text: 'Learn more',
            link,
          },
        ],
      }
    }

    useEffect(() => {
      if (featureFlagsStore.uk_compliance) countryStore.makeUKAvailable()
    }, [])

    return (
      <div>
        <ChooseNumberTabs
          store={chooseNumberTabsStore}
          className={styles.tabs}
          alertProps={showAlert || isUKSelected ? alertProps() : null}
          showCountrySelectOverTabs
          hideTabs={isUKComplianceNotCompleted || isUKComplianceCompleted}
          hideTabsContent={isUKComplianceNotCompleted}
        />

        {Boolean(
          activeTab === ITabsEnum.TOLL_FREE &&
            completedUseCases.length &&
            !chooseNumberTabsStore.isUKSelected
        ) && (
          <SelectField
            className={styles.selectWrap}
            labelText='Use case profile'
            items={[...items, laterItem]}
            setValue={setUseCase}
            value={selectedUseCaseId}
            btnProps={{
              size: 'extraLarge',
              className: styles.select,
            }}
            dropdownProps={{
              bottomActions: (onCloseMenu) => (
                <div
                  className={styles.addUseCaseLaterWrap}
                  onClick={() => {
                    setUseCase(assignLaterId)
                    onCloseMenu()
                  }}
                >
                  <Typography
                    tag='div'
                    ariaLabel='addUseCaseLater'
                    variant='body-md-regular'
                    color='var(--content-primary-primary)'
                  >
                    Add use case later
                  </Typography>
                  {selectedUseCaseId === assignLaterId && (
                    <Icon
                      icon='check1'
                      color='var(--content-brand-primary)'
                      className={styles.addUseCaseLaterIcon}
                    />
                  )}
                </div>
              ),
              textFieldProps: {
                InputProps: {
                  placeholder: 'Search',
                },
              },
            }}
          />
        )}
        {!isHideNextButton
          ? !isUKComplianceNotCompleted && (
              <NextButton
                text='Next'
                disabled={nextDisabled}
                onClick={nextClick}
                className={styles.nextButton}
              />
            )
          : null}
      </div>
    )
  }
)
